/**
 * AuthorityLevelsTab Component
 * Manages authority levels and their associated permissions
 * 
 * Created: 2026-01-27
 * 
 * Authority levels define what permissions users have based on their role's
 * default authority level or their personal authority level override.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const AuthorityLevelsTab = ({ isSuperadmin }) => {
  const { instance, accounts } = useMsal();
  const [authorityLevels, setAuthorityLevels] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [editedPermissions, setEditedPermissions] = useState([]);
  const [isWildcard, setIsWildcard] = useState(false);

  // Fetch authority levels and available permissions
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [levelsRes, permsRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/authority-levels'),
        authenticatedApiCall(instance, accounts, 'GET', '/authority-levels/permissions/available')
      ]);
      
      if (levelsRes.data.success) {
        setAuthorityLevels(levelsRes.data.data);
      }
      
      if (permsRes.data.success) {
        setAvailablePermissions(permsRes.data.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load authority levels');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle selecting an authority level for editing
  const handleSelectLevel = (level) => {
    setSelectedLevel(level);
    const perms = level.permissions || [];
    setIsWildcard(perms.includes('*'));
    setEditedPermissions(perms.filter(p => p !== '*'));
    setSuccessMessage('');
  };

  // Toggle a permission
  const togglePermission = (permName) => {
    setEditedPermissions(prev => {
      if (prev.includes(permName)) {
        return prev.filter(p => p !== permName);
      } else {
        return [...prev, permName];
      }
    });
  };

  // Toggle wildcard (all permissions)
  const toggleWildcard = () => {
    setIsWildcard(!isWildcard);
    if (!isWildcard) {
      setEditedPermissions([]);
    }
  };

  // Save changes
  const handleSave = async () => {
    if (!selectedLevel) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const permissions = isWildcard ? ['*'] : editedPermissions;
      
      const response = await authenticatedApiCall(
        instance, accounts, 'PUT',
        `/authority-levels/${selectedLevel.id}`,
        { permissions }
      );
      
      if (response.data.success) {
        setSuccessMessage(`${selectedLevel.name} permissions updated successfully`);
        // Update local state
        setAuthorityLevels(prev => prev.map(al => 
          al.id === selectedLevel.id ? { ...al, permissions } : al
        ));
        setSelectedLevel({ ...selectedLevel, permissions });
      }
    } catch (err) {
      console.error('Error saving:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Group permissions by resource
  const groupedPermissions = availablePermissions.reduce((acc, perm) => {
    const resource = perm.resource || 'other';
    if (!acc[resource]) acc[resource] = [];
    acc[resource].push(perm);
    return acc;
  }, {});

  // Styles
  const styles = {
    container: {
      padding: '20px',
    },
    header: {
      marginBottom: '20px',
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      marginBottom: '8px',
    },
    subtitle: {
      color: '#666',
      fontSize: '14px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '300px 1fr',
      gap: '24px',
    },
    levelsList: {
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      padding: '16px',
    },
    levelItem: (isSelected) => ({
      padding: '12px 16px',
      marginBottom: '8px',
      borderRadius: '6px',
      cursor: 'pointer',
      backgroundColor: isSelected ? '#dc3545' : 'white',
      color: isSelected ? 'white' : '#333',
      border: '1px solid #e9ecef',
      transition: 'all 0.2s',
    }),
    levelName: {
      fontWeight: '600',
      marginBottom: '4px',
    },
    levelHierarchy: {
      fontSize: '12px',
      opacity: 0.8,
    },
    levelPermCount: {
      fontSize: '11px',
      marginTop: '4px',
      opacity: 0.7,
    },
    editPanel: {
      backgroundColor: 'white',
      borderRadius: '8px',
      border: '1px solid #e9ecef',
      padding: '20px',
    },
    editHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '16px',
      borderBottom: '1px solid #e9ecef',
    },
    editTitle: {
      fontSize: '18px',
      fontWeight: '600',
    },
    saveButton: {
      padding: '8px 20px',
      backgroundColor: '#28a745',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: '500',
    },
    saveButtonDisabled: {
      padding: '8px 20px',
      backgroundColor: '#ccc',
      color: '#666',
      border: 'none',
      borderRadius: '4px',
      cursor: 'not-allowed',
      fontWeight: '500',
    },
    wildcardSection: {
      backgroundColor: '#fff3cd',
      border: '1px solid #ffc107',
      borderRadius: '6px',
      padding: '12px 16px',
      marginBottom: '20px',
    },
    wildcardLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer',
    },
    permissionGroup: {
      marginBottom: '20px',
    },
    groupTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#495057',
      marginBottom: '10px',
      textTransform: 'capitalize',
    },
    permissionGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '8px',
    },
    permissionItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      padding: '8px 12px',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
      fontSize: '13px',
    },
    permissionName: {
      fontWeight: '500',
      fontFamily: 'monospace',
    },
    permissionDesc: {
      fontSize: '11px',
      color: '#666',
      marginTop: '2px',
    },
    checkbox: {
      marginTop: '2px',
    },
    alert: (type) => ({
      padding: '12px 16px',
      borderRadius: '6px',
      marginBottom: '16px',
      backgroundColor: type === 'error' ? '#f8d7da' : '#d4edda',
      color: type === 'error' ? '#721c24' : '#155724',
      border: `1px solid ${type === 'error' ? '#f5c6cb' : '#c3e6cb'}`,
    }),
    viewOnlyBanner: {
      backgroundColor: '#e9ecef',
      padding: '12px 16px',
      borderRadius: '6px',
      marginBottom: '20px',
      color: '#495057',
      textAlign: 'center',
    },
    placeholder: {
      color: '#666',
      textAlign: 'center',
      padding: '40px',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.placeholder}>Loading authority levels...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Authority Level Permissions</h2>
        <p style={styles.subtitle}>
          Configure what permissions each authority level grants. Users inherit permissions from their
          effective authority level (either from their role's default or a personal override).
        </p>
      </div>

      {!isSuperadmin && (
        <div style={styles.viewOnlyBanner}>
          <strong>View Only</strong> - Only superadmins can modify authority level permissions
        </div>
      )}

      {error && <div style={styles.alert('error')}>{error}</div>}
      {successMessage && <div style={styles.alert('success')}>{successMessage}</div>}

      <div style={styles.grid}>
        {/* Authority Levels List */}
        <div style={styles.levelsList}>
          <h3 style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
            Authority Levels
          </h3>
          {/* Filter out System (id=1) - should never be editable from UI */}
          {authorityLevels.filter(level => level.name !== 'System').map(level => (
            <div
              key={level.id}
              style={styles.levelItem(selectedLevel?.id === level.id)}
              onClick={() => handleSelectLevel(level)}
            >
              <div style={styles.levelName}>{level.name}</div>
              <div style={styles.levelHierarchy}>
                Hierarchy: {level.hierarchy_level}
              </div>
              <div style={styles.levelPermCount}>
                {level.permissions?.includes('*') 
                  ? 'All permissions (*)' 
                  : `${(level.permissions || []).length} permissions`}
              </div>
            </div>
          ))}
        </div>

        {/* Edit Panel */}
        <div style={styles.editPanel}>
          {selectedLevel ? (
            <>
              <div style={styles.editHeader}>
                <div>
                  <div style={styles.editTitle}>{selectedLevel.name}</div>
                  <div style={{ fontSize: '13px', color: '#666' }}>
                    {selectedLevel.description}
                  </div>
                </div>
                {isSuperadmin && (
                  <button
                    style={saving ? styles.saveButtonDisabled : styles.saveButton}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              </div>

              {/* Wildcard Option */}
              <div style={styles.wildcardSection}>
                <label style={styles.wildcardLabel}>
                  <input
                    type="checkbox"
                    checked={isWildcard}
                    onChange={toggleWildcard}
                    disabled={!isSuperadmin}
                  />
                  <span>
                    <strong>Grant All Permissions (*)</strong>
                    <span style={{ fontSize: '12px', color: '#856404', marginLeft: '8px' }}>
                      Bypasses all permission checks
                    </span>
                  </span>
                </label>
              </div>

              {/* Individual Permissions */}
              {!isWildcard && Object.entries(groupedPermissions).map(([resource, perms]) => (
                <div key={resource} style={styles.permissionGroup}>
                  <div style={styles.groupTitle}>
                    {resource.replace(/_/g, ' ')}
                  </div>
                  <div style={styles.permissionGrid}>
                    {perms.map(perm => (
                      <div key={perm.id} style={styles.permissionItem}>
                        <input
                          type="checkbox"
                          style={styles.checkbox}
                          checked={editedPermissions.includes(perm.name)}
                          onChange={() => togglePermission(perm.name)}
                          disabled={!isSuperadmin}
                        />
                        <div>
                          <div style={styles.permissionName}>{perm.name}</div>
                          <div style={styles.permissionDesc}>{perm.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {isWildcard && (
                <div style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
                  Wildcard (*) grants all permissions. Uncheck to configure individual permissions.
                </div>
              )}
            </>
          ) : (
            <div style={styles.placeholder}>
              Select an authority level to view or edit its permissions
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthorityLevelsTab;
